import { Directive, Input, OnChanges, SimpleChanges, ViewContainerRef, inject } from '@angular/core';
import { SmartComponent } from '../class/smart-components/smart-component';
import { WidgetAlarmComponent } from '../components/widget-alarm/widget-alarm.component';
import { WidgetBlindComponent } from '../components/widget-blind/widget-blind.component';
import { WidgetContactComponent } from '../components/widget-contact/widget-contact.component';
import { WidgetDimmerComponent } from '../components/widget-dimmer/widget-dimmer.component';
import { WidgetLightComponent } from '../components/widget-light/widget-light.component';
import { WidgetThermostatComponent } from '../components/widget-thermostat/widget-thermostat.component';
import { SmartComponentTheme } from '../enum/enumSmartComponentTheme';
import { SmartComponentType } from '../enum/enumSmartComponentType';
import { WidgetCounterElectricComponent } from '../components/widget-counter-electric/widget-counter-electric.component';
import { WidgetCounterHotwaterComponent } from '../components/widget-counter-hotwater/widget-counter-hotwater.component';
import { WidgetCounterColdwaterComponent } from '../components/widget-counter-coldwater/widget-counter-coldwater.component';
import { WidgetCounterHeatingComponent } from '../components/widget-counter-heating/widget-counter-heating.component';
import { WidgetCounterDistributorComponent } from '../components/widget-counter-distributor/widget-counter-distributor.component';
import { WidgetCounterGasComponent } from '../components/widget-counter-gas/widget-counter-gas.component';
import { WidgetSensorComponent } from '../components/widget-sensor/widget-sensor.component';
import { WidgetWeatherStationComponent } from '../components/widget-weather-station/widget-weather-station.component';
import { WidgetLabelButtonComponent } from '../components/widget-label-button/widget-label-button.component';
import { WidgetScenarioComponent } from '../components/widget-scenario/widget-scenario.component';
import { WidgetPageBreakComponent } from "../components/widget-page-break/widget-page-break.component";

/**
 * This directive is rensponsible to render smart component inside a generic container by input list of smart component
 */
@Directive({ selector: "[appSmartComponentRenderingDirective]" })
export class SmartComponentRenderingDirectiveDirective implements OnChanges {
  private readonly _viewContainerRef = inject(ViewContainerRef);

  private _componentCreated: any[] = [];

  //Input smart component to render
  @Input() smartComponent: SmartComponent;
  @Input() isSupervision: boolean;
  @Input() isSupervisionModalOpen: boolean;
  @Input() isPopupHeader: boolean;
  @Input() isSendCommandPossible: boolean;
  @Input() imgPosition: string;
  @Input() showText: boolean;
  @Input() textColor: string;
  @Input() rotationIcon: number;
  @Input() zoom: number;
  //Environment to use when the widget is rendered outside of an environment route (ie. the configuration preview)
  @Input() idEnvironment: number;

  ngOnChanges(changes: SimpleChanges) {
    this.renderComponents(
      this.smartComponent,
      SmartComponentTheme.DIGITAL_PLACE,
    );
  }

  /**
   * This directive render components of generic smart component
   *
   * @param component smart component to render
   * @param theme Theme to on smart component
   */
  public renderComponents(
    component: SmartComponent,
    theme: SmartComponentTheme,
  ) {
    const componentsHostViewRef = this._viewContainerRef;

    componentsHostViewRef.clear();

    const compFactory = this.getComponentFactoryByComponentCode(
      component.idSmartComponentKind,
    );

    // if the component is recognized and the factory has been created we create the real component in the html and load into it the
    // relative API object with the command and state addresses
    if (compFactory) {
      const componentRef = componentsHostViewRef.createComponent(compFactory);

      this._componentCreated.push(componentRef);

      //Passing reference to smart component for specific smart component and rendering theme
      componentRef.setInput("smartComponent", component);
      componentRef.setInput("theme", theme);
      componentRef.setInput("isSupervision", this.isSupervision);
      componentRef.setInput("isSupervisionModalOpen", this.isSupervisionModalOpen);
      componentRef.setInput("isPopupHeader", this.isPopupHeader);
      componentRef.setInput("isSendCommandPossible", this.isSendCommandPossible);
      componentRef.setInput("imgPosition", this.imgPosition);
      componentRef.setInput("showText", this.showText);
      componentRef.setInput("textColor", this.textColor);
      componentRef.setInput("rotationIcon", this.rotationIcon);
      componentRef.setInput("zoom", this.zoom);
      componentRef.setInput("idEnvironment", this.idEnvironment);
    }
  }

  /**
   * Get component factory instance class by supervision component code
   *
   * @param componentCode Component code to get factory
   * @returns
   */
  private getComponentFactoryByComponentCode(
    smartComponentTypeKind: SmartComponentType,
  ): any {
    let componentClass: any = null;

    switch (smartComponentTypeKind) {
      case SmartComponentType.ELECTRIC:
        componentClass = WidgetCounterElectricComponent;
        break;
      case SmartComponentType.HOT_WATER:
        componentClass = WidgetCounterHotwaterComponent;
        break;
      case SmartComponentType.COLD_WATER:
        componentClass = WidgetCounterColdwaterComponent;
        break;
      case SmartComponentType.HEATING:
        componentClass = WidgetCounterHeatingComponent;
        break;
      case SmartComponentType.DISTRIBUTOR:
        componentClass = WidgetCounterDistributorComponent;
        break;
      case SmartComponentType.GAS:
        componentClass = WidgetCounterGasComponent;
        break;
      case SmartComponentType.LIGHT:
        componentClass = WidgetLightComponent;
        break;
      case SmartComponentType.DIMMER:
        componentClass = WidgetDimmerComponent;
        break;
      case SmartComponentType.BLINDS:
        componentClass = WidgetBlindComponent;
        break;
      case SmartComponentType.CONTACT:
        componentClass = WidgetContactComponent;
        break;
      case SmartComponentType.ALARM:
        componentClass = WidgetAlarmComponent;
        break;
      case SmartComponentType.THERMOSTAT:
        componentClass = WidgetThermostatComponent;
        break;
      case SmartComponentType.SENSOR:
        componentClass = WidgetSensorComponent;
        break;
      case SmartComponentType.WEATHER_STATION:
        componentClass = WidgetWeatherStationComponent;
        break;
      case SmartComponentType.LABEL:
        componentClass = WidgetLabelButtonComponent;
        break;
      case SmartComponentType.SCENARIO:
        componentClass = WidgetScenarioComponent;
        break;
      case SmartComponentType.PAGE_BREAK:
        componentClass = WidgetPageBreakComponent;
        break;
      default:
        break;
    }

    return componentClass;
  }
}
